/**
 * The hitch log, a dev tool: off until the debug overlay's HITCH LOG button
 * turns it on. While on, the gap between frames is measured against a
 * running median. A single skipped frame counts as a drop, a number for the
 * panel; a gap of two and a half frames or more, or 50 ms, is a hitch and
 * gets a row: when, the gap, the game loop's own time in the frame before
 * it, the packets dispatched, interface components woken, uiState keys
 * written and nameplate texts rebuilt in that frame (the context the hitch
 * hunt of 2026-10-20 had to reconstruct by hand), a GC guess from the heap
 * shrinking across the gap, and a cause: where Chrome's long-animation-frame
 * entry for the gap (Chrome 123+) puts the time, script, frame callbacks,
 * style and layout, or tasks before the render, and for that last phase how
 * late the frame after the gap began against its own frame time, which
 * tells a busy main thread from a frame the browser issued late. Each hitch
 * drops a performance.mark too, so a DevTools recording made alongside shows
 * where to look. Published to uiState.hitchLog once a second for
 * dev-hitch-panel; the whole log is kept here for the panel's copy. Network,
 * Player and ValtioController note their events through the functions
 * below, which cost one test of `on` when off.
 */
import { SERVER_PACKETS } from '@airmash/protocol';
import { uiState, type HitchEntry, type HitchLogState } from '../ui/UIState';

/** A gap over this, and over DROP_FACTOR times the median, is a skipped frame. */
const DROP_MIN_MS = 24;
const DROP_FACTOR = 1.5;
/** A gap over this, or over HITCH_FACTOR times the median, is a hitch with a row. */
const HITCH_MIN_MS = 50;
const HITCH_FACTOR = 2.5;
/** Frames the running median is taken over, and how often it is recomputed. */
const MEDIAN_WINDOW = 240;
const MEDIAN_EVERY = 60;
/** Hitches kept here for the copy, and how many the panel shows. */
const ENTRIES_KEPT = 500;
const ENTRIES_SHOWN = 40;
/** Packet types named per row, most frequent first. */
const PACKET_TYPES_SHOWN = 8;
/** Long-animation-frame entries kept for attributing hitches. */
const LONG_FRAMES_KEPT = 40;
/** A hitch still without a long-frame entry this long after its gap is described from its lateness alone. */
const ATTRIBUTION_WAIT_MS = 3000;
/**
 * A gap within this of how late its closing callback ran is the main
 * thread's: a task held the thread, and the browser had issued the frame
 * within a vsync of the task's start. A gap much longer than the lateness
 * came late itself, from the browser: a frame carries a time at most a
 * frame old (a missed frame is issued with the last vsync's time), so a
 * GPU-bound stretch reads as gaps of several frames with a lateness under
 * one. A frame and a half at 60 Hz, fixed rather than taken from the
 * measured frames, which a GPU-bound stretch lengthens too. Work inside a
 * frame callback leaves the next frame on time, since the browser issues
 * it after the callback returns; the long-frame entry's callbacks phase
 * catches that.
 */
const LATE_EXPLAINS_GAP_MS = 25;
/** A phase taking this share of the gap is named as where the gap went. */
const DOMINANT_SHARE = 0.5;
/** How often the panel's state is published while the log runs. */
const PUBLISH_INTERVAL_MS = 1000;

/** The name of each server packet code, for the context. */
const PACKET_NAMES = new Map<number, string>(Object.entries(SERVER_PACKETS).map(([name, code]) => [code, name]));

/** A hitch as recorded, with the gap's span for the attribution. */
interface HitchRecord extends HitchEntry {
  start: number;
  end: number;
}

/** One script of Chrome's long-animation-frame entry (Chrome 123+): those of 5 ms and over. */
interface LongAnimationFrameScript {
  readonly sourceURL?: string;
  readonly sourceFunctionName?: string;
  readonly invoker?: string;
  readonly startTime: number;
  readonly duration: number;
}

/**
 * Chrome's long-animation-frame entry (Chrome 123+). Its clock runs from the
 * first task after the previous rendering update to the end of this one,
 * idle time between included, so renderStart - startTime holds the tasks
 * before the render and any wait for the browser to begin it, while
 * blockingDuration counts only tasks over 50 ms and stays 0 across a wait.
 */
interface LongAnimationFrameEntry extends PerformanceEntry {
  readonly renderStart?: number;
  readonly styleAndLayoutStart?: number;
  readonly blockingDuration?: number;
  readonly scripts?: readonly LongAnimationFrameScript[];
}

function isLongAnimationFrame(entry: PerformanceEntry): entry is LongAnimationFrameEntry {
  return entry.entryType === 'long-animation-frame';
}

let on = false;
/** When the log started, which the rows' times count from. */
let startedAt = 0;
let publishTimer: ReturnType<typeof setInterval> | null = null;
let frames = 0;
let drops = 0;
let hitchCount = 0;
const entries: HitchRecord[] = [];
/** The last MEDIAN_WINDOW gaps, their median, and the frames since it was last taken. */
const recentGaps: number[] = [];
let medianGap = 1000 / 60;
let framesSinceMedian = 0;
/** The previous frame's start, for the gap; null after a hidden spell, whose keep-alive frames are no gap. */
let previousFrameStart: number | null = null;
/** The frame being timed for the loop, null between frames. */
let frameStartedAt: number | null = null;
/** The heap after the previous frame, for the GC guess. */
let previousHeap: number | null = null;
/** The frame's context so far, reset at each frame start. */
const packetCounts = new Map<number, number>();
let uiWakes = 0;
const uiKeys = new Set<string>();
let nameplates = 0;
let loopMs = 0;
/** The long-animation-frame entries not yet matched to a hitch. */
const longFrames: LongAnimationFrameEntry[] = [];
let longFrameObserver: PerformanceObserver | null = null;

/** The JS heap in MB, which only Chrome exposes. The debug overlay's memory line reads it too. */
export function heapMb(): number | null {
  const memory = (performance as { memory?: { usedJSHeapSize: number } }).memory;
  return memory === undefined ? null : memory.usedJSHeapSize / (1024 * 1024);
}

/** Starts the log: the counts from zero, the clock from now, the panel mounted by uiState.hitchLogOn. */
export function start(): void {
  if (on) return;
  on = true;
  resetLog(performance.now());
  startLongFrameObserver();
  uiState.hitchLogOn = true;
  publish();
  publishTimer = setInterval(publish, PUBLISH_INTERVAL_MS);
}

/** Stops the log and takes the panel down. */
export function stop(): void {
  if (!on) return;
  on = false;
  if (publishTimer !== null) {
    clearInterval(publishTimer);
    publishTimer = null;
  }
  longFrameObserver?.disconnect();
  longFrameObserver = null;
  longFrames.length = 0;
  uiState.hitchLogOn = false;
  uiState.hitchLog = null;
}

/** Empties the log and starts its clock again, the panel's CLEAR. */
export function clear(): void {
  if (!on) return;
  resetLog(performance.now());
  publish();
}

function resetLog(now: number): void {
  startedAt = now;
  frames = 0;
  drops = 0;
  hitchCount = 0;
  entries.length = 0;
  recentGaps.length = 0;
  medianGap = 1000 / 60;
  framesSinceMedian = 0;
  previousFrameStart = null;
  frameStartedAt = null;
  previousHeap = null;
  resetFrameContext();
}

function resetFrameContext(): void {
  packetCounts.clear();
  uiWakes = 0;
  uiKeys.clear();
  nameplates = 0;
  loopMs = 0;
}

/**
 * Marks a frame's start (Debug.startFrame calls it first thing): the gap
 * since the previous frame is judged with the context gathered since then,
 * and the context starts again for this frame.
 */
export function frameStart(): void {
  if (!on) return;
  const now = performance.now();
  frameStartedAt = now;
  frames++;
  if (previousFrameStart !== null && !document.hidden) {
    noteGap(now - previousFrameStart, now, lateness(now));
  }
  previousFrameStart = document.hidden ? null : now;
  resetFrameContext();
}

/**
 * How long after its frame time the frame began: document.timeline's
 * current time is the frame time the browser set at the start of this
 * rendering update, the one its frame callbacks get as their timestamp.
 * Null where the timeline keeps no number.
 */
function lateness(now: number): number | null {
  const frameTime = document.timeline.currentTime;
  return typeof frameTime === 'number' ? now - frameTime : null;
}

/** Marks the frame's end: the loop's own time, for the next gap's row. */
export function frameEnd(): void {
  if (!on || frameStartedAt === null) return;
  loopMs = performance.now() - frameStartedAt;
  frameStartedAt = null;
}

/** A server packet dispatched on the main thread, by code. */
export function notePacket(code: number): void {
  if (!on) return;
  packetCounts.set(code, (packetCounts.get(code) ?? 0) + 1);
}

/** A nameplate's text changed, which lays its glyphs out again; until the bitmap fonts of 2026-10-24 it re-rasterised a drop-shadowed canvas, the hitch source this column was added for. */
export function noteNameplateText(): void {
  if (!on) return;
  nameplates++;
}

/** A batch of uiState writes dispatched to the interface: how many components it woke, and the keys that moved. */
export function noteUiWake(components: number, keys: readonly string[]): void {
  if (!on) return;
  uiWakes += components;
  for (const key of keys) uiKeys.add(key);
}

/**
 * Takes a frame gap: into the running median, and into the drops or the
 * hitches when it is far above it. The median is recomputed every
 * MEDIAN_EVERY frames, so the sort is not per frame.
 */
function noteGap(gap: number, now: number, lateMs: number | null): void {
  recentGaps.push(gap);
  if (recentGaps.length > MEDIAN_WINDOW) recentGaps.shift();
  if (++framesSinceMedian >= MEDIAN_EVERY && recentGaps.length >= MEDIAN_EVERY) {
    framesSinceMedian = 0;
    const sorted = [...recentGaps].sort((a, b) => a - b);
    medianGap = sorted[sorted.length >> 1] ?? medianGap;
  }

  const heap = heapMb();
  const hitch = gap >= HITCH_MIN_MS || gap >= medianGap * HITCH_FACTOR;
  if (hitch) {
    hitchCount++;
    performance.mark(`hitch ${Math.round(gap)} ms`);
    entries.push({
      at: (now - startedAt) / 1000,
      gapMs: gap,
      medianMs: medianGap,
      loopMs,
      gcLikely: heap !== null && previousHeap !== null && heap < previousHeap - 1,
      packets: packetSummary(),
      uiWakes,
      uiKeys: [...uiKeys].join(', '),
      nameplates,
      lateMs,
      cause: null,
      start: now - gap,
      end: now,
    });
    if (entries.length > ENTRIES_KEPT) entries.shift();
  } else if (gap > DROP_MIN_MS && gap > medianGap * DROP_FACTOR) {
    drops++;
  }
  previousHeap = heap;
}

/** The frame's packets as "12 PLAYER_UPDATE, 1 SCORE_BOARD", most frequent first. */
function packetSummary(): string {
  return [...packetCounts]
    .sort((a, b) => b[1] - a[1])
    .slice(0, PACKET_TYPES_SHOWN)
    .map(([code, count]) => `${count} ${PACKET_NAMES.get(code) ?? code}`)
    .join(', ');
}

/**
 * Watches Chrome's long-animation-frame entries (Chrome 123+), whose phases
 * and scripts say where a frame of 50 ms or more went; the hitches are
 * matched to them at each publish. Nothing happens where the entry type is
 * unknown.
 */
function startLongFrameObserver(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  if (!PerformanceObserver.supportedEntryTypes.includes('long-animation-frame')) return;
  longFrameObserver = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (!isLongAnimationFrame(entry)) continue;
      longFrames.push(entry);
      if (longFrames.length > LONG_FRAMES_KEPT) longFrames.shift();
    }
  });
  longFrameObserver.observe({ type: 'long-animation-frame', buffered: true });
}

/** A duration for the cause text: a decimal under 10 ms, whole above. */
function ms(value: number): string {
  return value < 10 ? value.toFixed(1) : Math.round(value).toString();
}

/** A long frame's scripts as "name @ file 12 ms | ...". */
function describeScripts(scripts: readonly LongAnimationFrameScript[]): string {
  return scripts
    .map((script) => {
      const file = (script.sourceURL ?? '').split('/').pop()?.split('?')[0] || '?';
      return `${script.sourceFunctionName || script.invoker || '?'} @ ${file} ${ms(script.duration)} ms`;
    })
    .join(' | ');
}

/**
 * Names a hitch's cause. Chrome's long-frame entry, where one covers the
 * gap, says where the gap's time went, each phase clipped to the gap: the
 * scripts of 5 ms and over, frame callbacks, style, layout and paint, or
 * tasks before the render. Only that last phase is ambiguous, since the
 * entry's clock runs across idle time: how late the frame after the gap
 * began against its own frame time settles it, a callback about as late as
 * the gap is long having waited on a busy main thread, one within a frame
 * of its time having come as soon as the browser issued the frame, which
 * the compositor, the GPU or the display held. Without an entry the
 * lateness is all there is.
 */
function describeCause(hitch: HitchRecord, entry: LongAnimationFrameEntry | null): string {
  const late = hitch.lateMs;
  const lateByMainThread = late !== null && hitch.gapMs - late <= LATE_EXPLAINS_GAP_MS ? late : null;
  const callbackLate = late === null ? 'frame time unknown' : `callback ${ms(late)} ms after its frame time`;
  const issuedLate = 'browser issued the frame late (compositor, GPU or display)';
  if (entry === null) {
    const note = `no long-frame entry${hitch.gapMs < HITCH_MIN_MS ? ' under 50 ms' : ''}`;
    return lateByMainThread !== null
      ? `main thread late by ${ms(lateByMainThread)} ms; ${note}`
      : `${issuedLate}: ${callbackLate}; ${note}`;
  }
  const inGap = (from: number, to: number) => Math.max(0, Math.min(to, hitch.end) - Math.max(from, hitch.start));
  const scripts = entry.scripts ?? [];
  const renderStart = entry.renderStart ?? 0;
  const styleStart = entry.styleAndLayoutStart ?? 0;
  const end = entry.startTime + entry.duration;
  const tasks = inGap(entry.startTime, renderStart > 0 ? renderStart : end);
  const callbacks = renderStart > 0 ? inGap(renderStart, styleStart > 0 ? styleStart : end) : 0;
  const render = styleStart > 0 ? inGap(styleStart, end) : 0;
  const script = scripts.reduce((sum, s) => sum + inGap(s.startTime, s.startTime + s.duration), 0);
  const covered = tasks + callbacks + render;
  const outside = Math.max(0, hitch.gapMs - covered);
  const dominant = hitch.gapMs * DOMINANT_SHARE;
  let where: string;
  if (script >= dominant) where = `main thread: script ${ms(script)} ms`;
  else if (callbacks >= dominant)
    where = `main thread: frame callbacks ${ms(callbacks)} ms, the game loop ${ms(hitch.loopMs)} of it`;
  else if (render >= dominant) where = `main thread: style, layout and paint ${ms(render)} ms`;
  else if (tasks >= dominant) {
    where =
      lateByMainThread !== null
        ? `main thread: ${ms(tasks)} ms in tasks before the render with ${ms(script)} ms of script (GC, image decoding or an extension)`
        : `${issuedLate}: ${ms(tasks - script)} ms idle before the render, ${callbackLate}`;
  } else {
    where =
      lateByMainThread !== null
        ? `main thread late by ${ms(lateByMainThread)} ms, the long frame covering ${ms(covered)} ms of the gap`
        : `${issuedLate}: ${callbackLate}, the long frame covering ${ms(covered)} ms of the gap`;
  }
  const phases = `in the gap: tasks ${ms(tasks)}, callbacks ${ms(callbacks)}, style/layout/paint ${ms(render)}, script ${ms(script)}, outside the long frame ${ms(outside)}; blocking ${ms(entry.blockingDuration ?? 0)}`;
  return `${where}. ${phases}${scripts.length > 0 ? `. ${describeScripts(scripts)}` : ''}`;
}

/** Gives each hitch without a cause the long frame covering most of its gap, or, ATTRIBUTION_WAIT_MS after the gap with none, its lateness alone. */
function attributeHitches(): void {
  const now = performance.now();
  for (const hitch of entries) {
    if (hitch.cause !== null) continue;
    let best: LongAnimationFrameEntry | null = null;
    let bestOverlap = 0;
    for (const entry of longFrames) {
      const overlap = Math.min(entry.startTime + entry.duration, hitch.end) - Math.max(entry.startTime, hitch.start);
      if (overlap > bestOverlap) {
        best = entry;
        bestOverlap = overlap;
      }
    }
    if (best !== null) hitch.cause = describeCause(hitch, best);
    else if (now - hitch.end > ATTRIBUTION_WAIT_MS) hitch.cause = describeCause(hitch, null);
  }
}

/** An entry as the panel takes it, without the span. */
function publicEntry({ start: _start, end: _end, ...entry }: HitchRecord): HitchEntry {
  return entry;
}

/** The panel's state: the counts, and the latest rows newest first. */
function publish(): void {
  attributeHitches();
  const state: HitchLogState = {
    seconds: (performance.now() - startedAt) / 1000,
    frames,
    medianMs: medianGap,
    drops,
    hitches: hitchCount,
    entries: entries.slice(-ENTRIES_SHOWN).reverse().map(publicEntry),
    longFrames: longFrameObserver !== null,
  };
  uiState.hitchLog = state;
}

/** The whole log as text, oldest first, for the panel's copy. */
export function text(): string {
  attributeHitches();
  const seconds = (performance.now() - startedAt) / 1000;
  const lines = [
    `hitch log: ${seconds.toFixed(0)} s, ${frames} frames, median ${medianGap.toFixed(1)} ms, drops ${drops} (${(drops / Math.max(1, seconds)).toFixed(2)}/s), hitches ${hitchCount}${longFrameObserver === null ? ', no long-frame attribution in this browser' : ''}`,
    'at | gap | x median | loop | late | gc | packets | ui wakes | ui keys | nameplates | cause',
  ];
  for (const e of entries) {
    lines.push(
      `${e.at.toFixed(1)}s | ${e.gapMs.toFixed(1)} ms | x${(e.gapMs / e.medianMs).toFixed(1)} | ${e.loopMs.toFixed(1)} ms | ${e.lateMs === null ? '?' : `${e.lateMs.toFixed(1)} ms`} | ${e.gcLikely ? 'GC' : '-'} | ${e.packets || '-'} | ${e.uiWakes} | ${e.uiKeys || '-'} | ${e.nameplates} | ${e.cause ?? '-'}`,
    );
  }
  return lines.join('\n');
}
